// Log a dose. Picks from the family's medication catalogue.
//
// Empty catalogue → dashed "Add a medication" affordance + three one-tap
// suggestions (Vitamin D / Paracetamol / Ibuprofen). Each chip has a small
// edit affordance; the form lets the user create custom meds with their own
// unit, default dose, and interval. Saving a dose warns when the user's
// inside the medication's declared min_interval.

import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { Medication, MedicationEvent } from '@shared/types.ts';
import { Icon } from '../Icon.tsx';
import { SheetShell, FieldRow } from './SheetShell.tsx';
import {
  ConfirmDeletePanel,
  DateWithOptionalTime,
  DescriptionField,
  noonTodayLocal,
  timeIsSentinel,
  useConfirmDelete,
} from './fields.tsx';
import {
  useCreateMedication,
  useCreateMedicationEvent,
  useDeleteMedication,
  useMedicationEvents,
  useMedications,
  useUpdateMedication,
} from '../../api/medications.ts';
import { fmtDur } from '../../lib/format.ts';

type Props = {
  open: boolean;
  onClose: () => void;
  babyId: string | null;
  existing?: MedicationEvent | null;
  onDelete?: () => Promise<unknown> | void;
};

type EditorState = { mode: 'closed' } | { mode: 'new' } | { mode: 'edit'; med: Medication };

const SUGGESTIONS = [
  { name: 'Vitamin D',  unit: 'μg', default_dose: 10, min_interval_minutes: 24 * 60, color: '#d4a052' },
  { name: 'Paracetamol', unit: 'mg', default_dose: 60, min_interval_minutes: 4 * 60,  color: '#c96442' },
  { name: 'Ibuprofen',   unit: 'mg', default_dose: 50, min_interval_minutes: 6 * 60,  color: '#7a8aa8' },
] as const;

export function MedicationSheet({ open, onClose, babyId, existing, onDelete }: Props) {
  const { data: meds = [] } = useMedications();
  const { data: events = [] } = useMedicationEvents(babyId);
  const createMed = useCreateMedication();
  const updateMed = useUpdateMedication();
  const deleteMed = useDeleteMedication();
  const createEvent = useCreateMedicationEvent();

  const [medicationId, setMedicationId] = useState<string>('');
  const [doseDisplay, setDoseDisplay] = useState<string>('');
  const [unit, setUnit] = useState<string>('');
  const [occurredAt, setOccurredAt] = useState<number>(
    existing?.occurred_at ?? noonTodayLocal(),
  );
  const [showTime, setShowTime] = useState<boolean>(() =>
    existing ? !timeIsSentinel(existing.occurred_at) : true,
  );
  const [notes, setNotes] = useState<string>(existing?.notes ?? '');
  const [editor, setEditor] = useState<EditorState>({ mode: 'closed' });
  const confirm = useConfirmDelete();

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setMedicationId(existing.medication_id);
      setDoseDisplay(String(existing.dose));
      setUnit(existing.unit);
      setOccurredAt(existing.occurred_at);
      setNotes(existing.notes ?? '');
      setShowTime(!timeIsSentinel(existing.occurred_at));
    } else {
      const first = meds[0];
      setMedicationId(first?.id ?? '');
      setDoseDisplay(first?.default_dose != null ? String(first.default_dose) : '');
      setUnit(first?.unit ?? '');
      setOccurredAt(Math.floor(Date.now() / 1000));
      setNotes('');
      setShowTime(true);
    }
    setEditor({ mode: 'closed' });
    confirm.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing?.id, meds.length]);

  const picked = meds.find((m) => m.id === medicationId) ?? null;

  function pick(m: Medication) {
    setMedicationId(m.id);
    setUnit(m.unit);
    if (m.default_dose != null) setDoseDisplay(String(m.default_dose));
  }

  async function addSuggestion(s: (typeof SUGGESTIONS)[number]) {
    try {
      const { medication } = await createMed.mutateAsync(s);
      if (medication) pick(medication);
      toast.success(`${s.name} added`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not add');
    }
  }

  const parsedDose = Number(doseDisplay.replace(',', '.'));
  const validDose = Number.isFinite(parsedDose) && parsedDose > 0;
  const validPick = !!picked;

  // Find the most recent prior dose of the picked medication.
  const lastDoseSec = useMemo(() => {
    if (!picked) return null;
    const last = events.find(
      (e) => e.medication_id === picked.id && (!existing || e.id !== existing.id),
    );
    return last?.occurred_at ?? null;
  }, [events, picked, existing]);

  const intervalWarning = useMemo(() => {
    if (!picked?.min_interval_minutes || lastDoseSec == null) return null;
    const sinceMin = Math.floor((occurredAt - lastDoseSec) / 60);
    if (sinceMin >= picked.min_interval_minutes) return null;
    return {
      lastAgo: fmtDur(Math.max(0, sinceMin)),
      waitMore: fmtDur(picked.min_interval_minutes - sinceMin),
    };
  }, [picked, lastDoseSec, occurredAt]);

  async function save() {
    if (confirm.confirming && onDelete) {
      await onDelete();
      confirm.reset();
      onClose();
      return;
    }
    if (!validPick || !validDose || !picked) return;
    try {
      await createEvent.mutateAsync({
        medication_id: picked.id,
        dose: parsedDose,
        unit: unit || picked.unit,
        occurred_at: occurredAt,
        notes: notes.trim() || null,
      });
      toast.success(`${picked.name} · ${parsedDose} ${unit || picked.unit}`);
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    }
  }

  const emptyCatalogue = meds.length === 0;

  return (
    <SheetShell
      open={open}
      onClose={onClose}
      title={existing ? 'Edit dose' : 'Log medication'}
      tagline={picked ? `${picked.name}${picked.notes ? ` · ${picked.notes}` : ''}` : 'Pick a medication'}
      accent="accent"
      icon={(p) => <Icon.bottle {...p} />}
      primaryLabel={
        editor.mode !== 'closed' ? undefined : confirm.confirming ? 'Yes, delete' : 'Save'
      }
      primaryDanger={confirm.confirming || !!intervalWarning}
      primaryDisabled={!confirm.confirming && (!validPick || !validDose)}
      secondaryLabel={confirm.confirming ? 'Keep' : 'Cancel'}
      onPrimary={save}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Catalogue grid */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              padding: '0 4px',
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--text-3)',
                textTransform: 'uppercase',
                letterSpacing: 0.04,
              }}
            >
              Medication
            </span>
            {!emptyCatalogue && editor.mode === 'closed' && (
              <button
                type="button"
                onClick={() => setEditor({ mode: 'new' })}
                className="lt-btn lt-btn-ghost"
                style={{ padding: '4px 10px', fontSize: 11, color: 'var(--text-2)' }}
              >
                <Icon.plus s={12} /> New
              </button>
            )}
          </div>

          {emptyCatalogue && editor.mode === 'closed' ? (
            <EmptyCatalogue
              onPickSuggestion={addSuggestion}
              onCustom={() => setEditor({ mode: 'new' })}
            />
          ) : editor.mode === 'closed' ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                gap: 6,
              }}
            >
              {meds.map((m) => (
                <MedChip
                  key={m.id}
                  med={m}
                  selected={m.id === medicationId}
                  onPick={() => pick(m)}
                  onEdit={() => setEditor({ mode: 'edit', med: m })}
                />
              ))}
            </div>
          ) : (
            <MedicationForm
              initial={editor.mode === 'edit' ? editor.med : null}
              saving={createMed.isPending || updateMed.isPending}
              onCancel={() => setEditor({ mode: 'closed' })}
              onSubmit={async (vars) => {
                try {
                  if (editor.mode === 'edit') {
                    await updateMed.mutateAsync({ id: editor.med.id, patch: vars });
                    toast.success('Saved');
                  } else {
                    const { medication } = await createMed.mutateAsync(vars);
                    if (medication) pick(medication);
                    toast.success(`${vars.name} added`);
                  }
                  setEditor({ mode: 'closed' });
                } catch (e) {
                  toast.error(e instanceof Error ? e.message : 'Save failed');
                }
              }}
              onArchive={
                editor.mode === 'edit'
                  ? async () => {
                      try {
                        await deleteMed.mutateAsync(editor.med.id);
                        toast.success('Medication archived');
                        if (editor.med.id === medicationId) {
                          setMedicationId('');
                          setDoseDisplay('');
                          setUnit('');
                        }
                        setEditor({ mode: 'closed' });
                      } catch (e) {
                        toast.error(e instanceof Error ? e.message : 'Archive failed');
                      }
                    }
                  : undefined
              }
            />
          )}
        </div>

        {/* Dose entry — only when not in editor */}
        {editor.mode === 'closed' && picked && (
          <>
            <FieldRow
              label="Dose"
              value={
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <input
                    inputMode="decimal"
                    pattern="[0-9.,]*"
                    placeholder={picked.default_dose != null ? String(picked.default_dose) : '—'}
                    value={doseDisplay}
                    onChange={(e) => {
                      const cleaned = e.target.value.replace(/[^0-9.,]/g, '').replace(/[.,]+/g, '.');
                      setDoseDisplay(cleaned);
                    }}
                    className="lt-tabular"
                    style={{ width: 80, fontSize: 22, color: 'var(--text-1)' }}
                  />
                  <input
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    maxLength={6}
                    placeholder={picked.unit}
                    style={{ width: 60, fontSize: 14, color: 'var(--text-3)' }}
                  />
                </div>
              }
            />

            <DateWithOptionalTime
              occurredAt={occurredAt}
              onChange={setOccurredAt}
              showTime={showTime}
              onToggleTime={setShowTime}
            />

            {intervalWarning && (
              <div
                style={{
                  padding: 12,
                  borderRadius: 12,
                  background: 'color-mix(in oklab, var(--danger) 12%, transparent)',
                  border: '1px solid color-mix(in oklab, var(--danger) 30%, transparent)',
                  fontSize: 13,
                  color: 'var(--text-1)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                }}
              >
                <Icon.bell s={16} style={{ color: 'var(--danger)', marginTop: 2, flexShrink: 0 }} />
                <div>
                  Last dose was <strong>{intervalWarning.lastAgo}</strong> ago. Recommended to wait
                  another <strong>{intervalWarning.waitMore}</strong> before the next dose.
                </div>
              </div>
            )}

            <DescriptionField value={notes} onChange={setNotes} placeholder="brand, reaction, why" />

            {onDelete && !confirm.confirming && (
              <button
                type="button"
                className="lt-btn lt-btn-ghost"
                style={{ width: '100%', padding: '10px 0', fontSize: 13, color: 'var(--danger)' }}
                onClick={confirm.startConfirm}
              >
                <Icon.trash s={14} /> Delete this dose
              </button>
            )}
            {confirm.confirming && (
              <ConfirmDeletePanel message="This is permanent. The dose will disappear from the log." />
            )}
          </>
        )}
      </div>
    </SheetShell>
  );
}

function MedChip({
  med,
  selected,
  onPick,
  onEdit,
}: {
  med: Medication;
  selected: boolean;
  onPick: () => void;
  onEdit: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'stretch',
        background: selected ? 'var(--accent-soft)' : 'var(--bg-card)',
        border: selected ? '1px solid var(--accent)' : '1px solid var(--border)',
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={onPick}
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 12px',
          background: 'transparent',
          border: 'none',
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            background: med.color ?? 'var(--text-3)',
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-1)' }}>{med.name}</span>
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onEdit();
        }}
        aria-label={`Edit ${med.name}`}
        style={{
          padding: '0 10px',
          color: 'var(--text-3)',
          background: 'transparent',
          border: 'none',
          borderLeft: '1px solid var(--border)',
          cursor: 'pointer',
        }}
      >
        <Icon.edit s={12} />
      </button>
    </div>
  );
}

function EmptyCatalogue({
  onPickSuggestion,
  onCustom,
}: {
  onPickSuggestion: (s: (typeof SUGGESTIONS)[number]) => void;
  onCustom: () => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <button
        type="button"
        onClick={onCustom}
        style={{
          padding: '16px 14px',
          borderRadius: 14,
          background: 'transparent',
          border: '1px dashed var(--border-strong)',
          color: 'var(--text-2)',
          fontSize: 14,
          fontWeight: 500,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        <Icon.plus s={16} /> Add a medication
      </button>
      <div style={{ fontSize: 11, color: 'var(--text-3)', padding: '4px 4px 0' }}>
        Common starters — tap to add
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {SUGGESTIONS.map((s) => (
          <button
            type="button"
            key={s.name}
            onClick={() => onPickSuggestion(s)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 12px',
              borderRadius: 999,
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              fontSize: 12,
              color: 'var(--text-2)',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 999,
                background: s.color,
              }}
            />
            <Icon.plus s={11} /> {s.name}
          </button>
        ))}
      </div>
    </div>
  );
}

function MedicationForm({
  initial,
  saving,
  onSubmit,
  onCancel,
  onArchive,
}: {
  initial: Medication | null;
  saving: boolean;
  onSubmit: (vars: {
    name: string;
    unit: string;
    default_dose?: number | null;
    min_interval_minutes?: number | null;
    color?: string | null;
  }) => Promise<void> | void;
  onCancel: () => void;
  onArchive?: () => Promise<void> | void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [unit, setUnit] = useState(initial?.unit ?? 'mg');
  const [doseStr, setDoseStr] = useState(initial?.default_dose != null ? String(initial.default_dose) : '');
  const [intervalHours, setIntervalHours] = useState(
    initial?.min_interval_minutes != null ? String(initial.min_interval_minutes / 60) : '',
  );
  const [color, setColor] = useState(initial?.color ?? '#7a8aa8');

  const COLORS = ['#c96442', '#d4a052', '#7a8aa8', '#6b8e5a', '#b09ac7', '#c97e96'];
  const dose = Number(doseStr.replace(',', '.'));
  const intervalMin = Number(intervalHours.replace(',', '.')) * 60;
  const canSave = name.trim().length > 0 && unit.trim().length > 0;

  return (
    <div
      style={{
        padding: '14px 14px',
        borderRadius: 14,
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>
        {initial ? 'Edit medication' : 'New medication'}
      </div>

      <Field label="Name">
        <input
          value={name}
          autoFocus
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Paracetamol"
          style={{ width: '100%', fontSize: 15 }}
        />
      </Field>

      <div style={{ display: 'flex', gap: 10 }}>
        <Field label="Unit" style={{ flex: '0 0 90px' }}>
          <input
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            placeholder="mg / ml / μg / drops"
            maxLength={8}
            style={{ width: '100%', fontSize: 15 }}
          />
        </Field>
        <Field label="Default dose" style={{ flex: 1 }}>
          <input
            inputMode="decimal"
            value={doseStr}
            onChange={(e) =>
              setDoseStr(e.target.value.replace(/[^0-9.,]/g, '').replace(/[.,]+/g, '.'))
            }
            placeholder="optional"
            style={{ width: '100%', fontSize: 15 }}
          />
        </Field>
      </div>

      <Field label="Min interval (hours)">
        <input
          inputMode="decimal"
          value={intervalHours}
          onChange={(e) =>
            setIntervalHours(e.target.value.replace(/[^0-9.,]/g, '').replace(/[.,]+/g, '.'))
          }
          placeholder="e.g. 4 for paracetamol, 24 for vit D"
          style={{ width: '100%', fontSize: 15 }}
        />
      </Field>

      <Field label="Colour">
        <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
          {COLORS.map((c) => (
            <button
              type="button"
              key={c}
              onClick={() => setColor(c)}
              style={{
                width: 26,
                height: 26,
                borderRadius: 999,
                background: c,
                border: c === color ? '2px solid var(--text-1)' : '2px solid transparent',
                cursor: 'pointer',
              }}
              aria-label={`color ${c}`}
            />
          ))}
        </div>
      </Field>

      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button
          type="button"
          className="lt-btn lt-btn-soft"
          style={{ flex: '0 0 90px', padding: '10px 0' }}
          onClick={onCancel}
        >
          Cancel
        </button>
        {onArchive && (
          <button
            type="button"
            className="lt-btn lt-btn-ghost"
            style={{ flex: 1, padding: '10px 0', color: 'var(--danger)' }}
            onClick={onArchive}
          >
            <Icon.trash s={14} /> Archive
          </button>
        )}
        <button
          type="button"
          className="lt-btn lt-btn-primary"
          style={{ flex: 1, padding: '10px 0' }}
          disabled={!canSave || saving}
          onClick={() =>
            onSubmit({
              name: name.trim(),
              unit: unit.trim(),
              default_dose: doseStr ? dose : null,
              min_interval_minutes: intervalHours ? Math.round(intervalMin) : null,
              color,
            })
          }
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
  style,
}: {
  label: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div style={style}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--text-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.04,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}
